import { Button, Modal, Form, Typography, useI18nContext } from "@procore/core-react";
import type { MutableRefObject } from "react";
import React, { useState, useCallback } from "react";
import { ErrorBanner } from "../../error-banner";
import type { FormikProps } from "formik";
import * as yup from "yup";
import type { CreateSavedViewPayload } from "@laborchart-modules/lc-core-api/dist/api/saved-views/create-saved-view";
import type { SelectValue, CustomField } from "@/react/prop-types";
import { SavedViewStore } from "@/stores/saved-view-store.core";
import { useToastAlertContext } from "@procore/toast-alert";
import type { TableApi, DataTableConfig } from "@procore/data-table";
import type { SavedView } from "@laborchart-modules/common";
import { useStreamPeopleOptions, useGetGroupOptionsQuery } from "../../common/queries/queries";
import type { SerializedFindUsersSavedViews } from "@laborchart-modules/lc-core-api/dist/api/saved-views/find-users-saved-views";

export type SavedViewModalProps = {
   tableApi?: TableApi;
   conversionFunction: (
      config: DataTableConfig,
      columnHeadersMap: { [key: string]: string },
      filterNameMaps: { [key: string]: string },
      filterFieldMap: { [key: string]: string },
      filterRendererMap: { [key: string]: string },
      customFields?: CustomField[],
      page?: string,
   ) => Partial<SavedView>;
   page: string;
   customFields?: CustomField[];
   columnHeadersMap: { [key: string]: string };
   filterNameMaps: { [key: string]: string };
   filterFieldMap: { [key: string]: string };
   filterRendererMap: { [key: string]: string };
   configKey: string;
   searchString?: MutableRefObject<string>;
   groupId: string;
   groupOptions?: Array<{ id: string; label: string }>;
   viewConfig?: object;
};
export const CreateSavedViewModal = (props: SavedViewModalProps) => {
   const {
      conversionFunction,
      page,
      columnHeadersMap,
      filterNameMaps,
      filterFieldMap,
      filterRendererMap,
      searchString,
      customFields,
      groupId,
      configKey,
      viewConfig,
   } = props;
   const I18n = useI18nContext();
   const { showToast } = useToastAlertContext();
   const [open, setOpen] = useState(false);
   const [error, setError] = useState(false);
   const [savedViews, setSavedViews] = useState<SerializedFindUsersSavedViews>([]);

   const handleOpen = useCallback(async () => {
      try {
         // Fetch existing saved views for the user when modal opens
         // later use it to check for duplicate names
         const result = await SavedViewStore.getUsersSavedViews().payload;
         setSavedViews(result.data || []);
         setOpen(true);
      } catch (err) {
         console.error("ERROR in Fetching User Saved Views:", err);
      }
   }, []);

   const { data: people, isLoading: loadingPeople } = useStreamPeopleOptions(groupId);
   const { data: groups, isLoading: loadingGroups } = useGetGroupOptionsQuery();

   const getLoadingState = (shareType?: string) => {
      if (!shareType) return true;

      return shareType === "people" ? loadingPeople : loadingGroups;
   };

   const handleSubmit = async (formValues: any) => {
      try {
         const localStorageConfig = JSON.parse(localStorage.getItem(configKey) ?? "{}");
         const saved_view = {
            chip_filters: [],
            view_config: {
               column_headers: [],
               sort_by: "name",
               sort_direction: "ascending",
               ...viewConfig,
            },
            page_specific: {},
            ...conversionFunction(
               localStorageConfig,
               columnHeadersMap,
               filterNameMaps,
               filterFieldMap,
               filterRendererMap,
               customFields,
               page,
            ),
            page: page,
         };
         const shared_with_ids = formValues.shared_with.map((value: SelectValue<string>) => {
            return value.id;
         });
         const payload = {
            ...saved_view,
            share_type: formValues.share_type.value,
            name: formValues.name,
            group_ids: formValues.share_type?.value == "groups" ? shared_with_ids : [],
            shared_with_people_ids: formValues.share_type?.value == "people" ? shared_with_ids : [],
            search: searchString?.current || undefined,
         };
         await SavedViewStore.createSavedView(payload as CreateSavedViewPayload).payload;
         setOpen(false);
         showToast.success(I18n.t("views.company.workforce_planning.saved_views.success_toast"));
      } catch (err) {
         console.error("ERROR with CreateSavedViewModal:", err);
         setError(true);
      }
   };
   const shareTypeOptions = [
      {
         id: 0,
         value: "private",
         label: I18n.t("views.company.workforce_planning.saved_views.private"),
      },
      {
         id: 1,
         value: "people",
         label: I18n.t("views.company.workforce_planning.saved_views.individuals"),
      },
      {
         id: 2,
         value: "groups",
         label: I18n.t("views.company.workforce_planning.saved_views.groups"),
      },
   ];
   const shareWithSelectPlaceholder = (shareType: string) => {
      const translation =
         shareType === "people"
            ? I18n.t("views.company.workforce_planning.saved_views.select_people")
            : I18n.t("views.company.workforce_planning.saved_views.select_groups");

      const capitalizedShareType = shareType[0].toUpperCase() + shareType.slice(1);

      return translation.includes("missing") ? "Select " + capitalizedShareType : translation;
   };

   return (
      <>
         <Button variant="tertiary" onClick={handleOpen}>
            {I18n.t("views.company.workforce_planning.saved_views.save_view")}
         </Button>
         <Modal open={open} onClose={() => setOpen(false)} width="md">
            <Form
               initialValues={{
                  share_type: shareTypeOptions[0],
                  shared_with: [],
               }}
               view="create"
               onSubmit={handleSubmit}
               validationSchema={yup.object().shape({
                  name: yup
                     .string()
                     .required(
                        I18n.t("views.company.workforce_planning.validations.required_field"),
                     )
                     .test(
                        "unique-name",
                        I18n.t(
                           "views.company.workforce_planning.saved_views.duplicate_name_error",
                           {
                              view_name: "${value}",
                           },
                        ),
                        function (value) {
                           if (!value) return true;
                           return !savedViews.some(
                              (view) =>
                                 view.page === page &&
                                 view.name.trim().toLowerCase() === value.trim().toLowerCase(),
                           );
                        },
                     ),
                  share_type: yup
                     .object()
                     .required(
                        I18n.t("views.company.workforce_planning.validations.required_field"),
                     ),
               })}
            >
               {
                  /* istanbul ignore next */
                  /* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */
                  (formikProps: FormikProps<any>) => {
                     const { share_type } = formikProps.values;
                     return (
                        <Form.Form>
                           <Modal.Header onClose={() => setOpen(false)}>
                              {I18n.t("views.company.workforce_planning.saved_views.save_view")}
                           </Modal.Header>
                           <Modal.Body>
                              {error && (
                                 <ErrorBanner
                                    title={I18n.t("views.company.workforce_planning.error")}
                                    content={I18n.t(
                                       "views.company.workforce_planning.saved_views.error_banner",
                                    )}
                                 />
                              )}
                              <Modal.Section>
                                 <Typography>
                                    {I18n.t(
                                       "views.company.workforce_planning.saved_views.info_text",
                                    )}
                                 </Typography>
                                 <Form.Row>
                                    <Form.Field
                                       name="name"
                                       label={I18n.t(
                                          "views.company.workforce_planning.saved_views.view_name",
                                       )}
                                       required
                                       colWidth={12}
                                    />
                                 </Form.Row>
                                 <Form.Row>
                                    <Form.RadioButtons
                                       name="share_type"
                                       required
                                       label={I18n.t(
                                          "views.company.workforce_planning.saved_views.share_type",
                                       )}
                                       options={shareTypeOptions}
                                       /* @ts-expect-error Core React Form.RadioButtons needs to add this as a valid prop */
                                       onChange={() => formikProps.setFieldValue("shared_with", [])}
                                    />
                                    {share_type?.value != "private" && (
                                       <Form.MultiSelect
                                          colStart={7}
                                          name="shared_with"
                                          label={I18n.t(
                                             "views.company.workforce_planning.saved_views.share_with",
                                          )}
                                          required
                                          placeholder={shareWithSelectPlaceholder(
                                             share_type?.value,
                                          )}
                                          options={
                                             share_type?.value == "people"
                                                ? (people as any)
                                                : (groups as any)
                                          }
                                          loading={getLoadingState(share_type?.value)}
                                          disabled={share_type?.value == "private"}
                                       />
                                    )}
                                 </Form.Row>
                              </Modal.Section>
                           </Modal.Body>
                           <Modal.Footer>
                              <Modal.FooterButtons>
                                 <Button variant="tertiary" onClick={() => setOpen(false)}>
                                    {I18n.t("views.company.workforce_planning.cancel")}
                                 </Button>
                                 <Button variant="primary" type="submit">
                                    {I18n.t("views.company.workforce_planning.save")}
                                 </Button>
                              </Modal.FooterButtons>
                           </Modal.Footer>
                        </Form.Form>
                     );
                  }
               }
            </Form>
         </Modal>
      </>
   );
};
